import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Loader2, CheckCircle, XCircle, Shield, Target, AlertTriangle,
  RefreshCw, Users, Clock, Camera, Mail, Bell,
  TrendingUp, FileText, Zap
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const AdminMorningReport = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [redFlags, setRedFlags] = useState([]);
  const [scoutAlerts, setScoutAlerts] = useState([]);
  const [scanning, setScanning] = useState(false);

  const loadReport = useCallback(async () => {
    try {
      const [reportRes, flagsRes, scoutsRes] = await Promise.all([
        api.get('/school/admin/morning-report'),
        api.get('/school/wingman/red-flags'),
        api.get('/school/wingman/scout-alerts')
      ]);
      setReport(reportRes.data.report);
      setRedFlags(flagsRes.data.flags || []);
      setScoutAlerts(scoutsRes.data.alerts || []);
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  const runScans = async () => {
    setScanning(true);
    try {
      const [redRes, scoutRes] = await Promise.all([
        api.post('/school/wingman/scan-red-flags'),
        api.post('/school/wingman/scan-elite-scouts')
      ]);
      const newFlags = redRes?.data?.new_flags ?? redRes?.data?.flags?.length ?? 0;
      const newScouts = scoutRes?.data?.new_alerts ?? scoutRes?.data?.alerts?.length ?? 0;
      toast.success(`Wingman scans complete — ${newFlags} red flag(s), ${newScouts} scout alert(s)`);
      await loadReport();
    } catch (err) {
      toast.error('Scan failed — try again');
    } finally {
      setScanning(false);
    }
  };

  const resolveFlag = async (flagId) => {
    try {
      await api.post(`/school/wingman/resolve-flag/${flagId}`);
      toast.success('Flag resolved');
      setRedFlags(prev => prev.filter(f => f.id !== flagId));
    } catch (err) {
      toast.error('Failed to resolve');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const s = report?.summary || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-16" data-testid="admin-morning-report">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white" data-testid="report-title">Morning Report</h1>
                <div className="text-xs text-gray-500">{report?.date} | {report?.school}</div>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={runScans} disabled={scanning} className="bg-orange-500 hover:bg-orange-600 text-black font-bold" data-testid="run-scans-btn">
              {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Run Wingman Scans
            </Button>
            <Button onClick={loadReport} variant="outline" className="border-gray-700 text-gray-400">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          <Card className="bg-[#111] border-gray-800/50">
            <CardContent className="p-4 text-center">
              <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <div className="text-2xl font-black text-white">{s.total_enrolled}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-widest">Enrolled</div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-green-500/20">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <div className="text-2xl font-black text-green-400">{s.present}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-widest">Present</div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-red-500/20">
            <CardContent className="p-4 text-center">
              <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
              <div className="text-2xl font-black text-red-400">{s.absent}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-widest">Absent</div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-orange-500/20">
            <CardContent className="p-4 text-center">
              <Camera className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <div className="text-2xl font-black text-white">{s.biometric_verified}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-widest">Biometric</div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-gray-800/50">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 text-cyan-500 mx-auto mb-1" />
              <div className="text-2xl font-black text-white">{s.attendance_rate}%</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-widest">Rate</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Attendance Lists */}
          <div className="lg:col-span-2 space-y-6">
            {/* Present */}
            <div>
              <h2 className="text-sm font-bold text-green-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Present ({report?.present_students?.length || 0})
              </h2>
              <div className="space-y-2">
                {(report?.present_students || []).map((p, i) => (
                  <div key={i} className="bg-[#111] border border-green-500/10 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium text-sm">{p.name}</div>
                      <div className="text-xs text-gray-500">Grade {p.grade_level} | {p.sport || 'Scholar'}</div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        {p.verification_method === 'eyepop_biometric' ? (
                          <Camera className="w-3 h-3 text-orange-500" />
                        ) : (
                          <Clock className="w-3 h-3 text-gray-500" />
                        )}
                        <span className="text-xs text-gray-500">{p.verification_method}</span>
                      </div>
                      {p.verification_confidence && (
                        <div className="text-[10px] text-gray-600">{(p.verification_confidence * 100).toFixed(1)}% conf</div>
                      )}
                      <div className="text-[10px] text-gray-600">
                        {p.check_in_time ? new Date(p.check_in_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                  </div>
                ))}
                {(report?.present_students || []).length === 0 && (
                  <div className="text-gray-600 text-sm text-center py-4">No check-ins yet today</div>
                )}
              </div>
            </div>

            {/* Absent */}
            <div>
              <h2 className="text-sm font-bold text-red-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Absent ({report?.absent_students?.length || 0})
              </h2>
              <div className="space-y-2">
                {(report?.absent_students || []).map((a, i) => (
                  <div key={i} className="bg-[#111] border border-red-500/10 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                      <XCircle className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium text-sm">{a.name}</div>
                      <div className="text-xs text-gray-500">Grade {a.grade_level} | {a.sport || 'Scholar'}</div>
                    </div>
                    <div className="text-xs text-red-400">NOT CHECKED IN</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Wingman Alerts */}
          <div className="space-y-6">
            {/* Red Flags */}
            <div>
              <h2 className="text-sm font-bold text-red-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Red Flags ({redFlags.length})
              </h2>
              {redFlags.length > 0 ? (
                <div className="space-y-2">
                  {redFlags.map((flag) => (
                    <Card key={flag.id} className="bg-red-500/5 border-red-500/20">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                            flag.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                          }`}>{flag.severity}</span>
                          <span className="text-[10px] text-gray-600 font-mono">{flag.type}</span>
                        </div>
                        <div className="text-white font-medium text-sm">{flag.student_name}</div>
                        <div className="text-xs text-red-400 mt-1">GPA: {flag.data?.core_gpa} (min: {flag.data?.threshold})</div>
                        <div className="bg-gray-900/50 rounded p-2 mt-2">
                          <div className="text-[10px] text-orange-500 font-bold mb-1">WINGMAN ACTION</div>
                          <div className="text-xs text-gray-400">{flag.wingman_action?.message_draft?.substring(0, 120)}...</div>
                        </div>
                        <Button onClick={() => resolveFlag(flag.id)} size="sm" variant="outline" className="w-full mt-2 border-red-500/30 text-red-400 text-xs h-7">
                          Mark Resolved
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="bg-[#111] border border-gray-800/50 rounded-lg p-4 text-center">
                  <Shield className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <div className="text-sm text-gray-400">No active red flags</div>
                </div>
              )}
            </div>

            {/* Scout Alerts */}
            <div>
              <h2 className="text-sm font-bold text-cyan-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" /> Scout Alerts ({scoutAlerts.length})
              </h2>
              {scoutAlerts.length > 0 ? (
                <div className="space-y-2">
                  {scoutAlerts.map((alert) => (
                    <Card key={alert.id} className="bg-cyan-500/5 border-cyan-500/20">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400">ELITE</span>
                          <span className="text-lg font-black text-cyan-400">{alert.data?.score}%</span>
                        </div>
                        <div className="text-white font-medium text-sm">{alert.student_name}</div>
                        <div className="text-xs text-gray-500">{alert.data?.drill_type?.charAt(0).toUpperCase() + alert.data?.drill_type?.slice(1)} Drill | GPA: {alert.data?.core_gpa}</div>
                        <div className="bg-gray-900/50 rounded p-2 mt-2">
                          <div className="text-[10px] text-orange-500 font-bold mb-1">WINGMAN ACTION</div>
                          <div className="text-xs text-gray-400 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> Recruiter email draft ready
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="bg-[#111] border border-gray-800/50 rounded-lg p-4 text-center">
                  <Target className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                  <div className="text-sm text-gray-400">No elite scout alerts</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMorningReport;
